
import { IRatesOfReturn, IRetirementSavingsPersonInputs } from 'app/calcs/retirementSavings'
import { dateAtAge, IRetirementPerson, isValidPerson } from 'app/redux/Profile/model'
import * as utils from 'app/utils'
import * as DateCalcs from 'app/utils/DateCalcs'
import { ISavingsBalances, ISavingsWorkbookRunCalculationsRequest, runCalculator, SavingsDAOPersonExtractors } from 'app/workbookDAOs/SavingsWorkbookDAO'
import * as _ from 'lodash'
import * as moment from 'moment'

interface IRetirementSavingsAnalyzerSavings {
  startContributions: moment.Moment
  stopContributions: moment.Moment
  savingsDuration: moment.Duration
  annualContributions: number
  totalFixedBalances: number
  totalNewSavings: number
  totalSavings: number
}

interface IRetirementSavingsAnalyzerWithdrawals {
  monthlyWithdrawalAmount: number
  startWithdrawals: moment.Moment
  stopWithdrawals: moment.Moment
  withdrawalsDuration: moment.Duration
}

interface IXIRetirementSavingsAnalyzerBalance {
  when: moment.Moment
  amount: number
  newSavings: number
  desiredWithdrawal?: number
  allowedWithdrawal: number
}
interface IRetirementSavingsAnalyzerBalance extends ISavingsBalances {
  desiredWithdrawal: number
}

const debug = utils.makeDebug(false)
const nonZeroBalance = (d: ISavingsBalances) => 0 !== d.amount
const nonZeroAllowedWithdrawal = (d: ISavingsBalances) => 0 !== d.allowedWithdrawal
const nonZeroAmount = (d: ISavingsBalances) => 0 != d.amount
function whenSameDate(when: moment.Moment) {
  return (d: ISavingsBalances) => when.isSame(d.when)
}

function newSavingsAsOfDuration(contributions: number, duration: moment.Duration): number {
  return contributions * (utils.durationAsFraction(duration) + 1 / 12)
}

/**
 * Returns some chart data for the savings.
 *
 * @param balances
 *
 * @returns An array of x/y data { x: when, y: amount }
 */
function getChartData(analysis) {
  return analysis.savingsAndWithdrawalsSlice.map((d) => {
    const n = []
    if (d.newSavings) { n.push('new savings: ' + utils.toCurrency(d.newSavings)) }
    if (d.allowedWithdrawal) { n.push('withdrawal: ' + utils.toCurrency(d.allowedWithdrawal)) }
    n.push('<b>balance: ' + utils.toCurrency(d.amount) + '</b>')
    return {
      x: utils.fractionalYear(d.when),
      y: d.amount,
      extraStuff: n.join(', ')
    }
  })
}

interface IAnalyzeSavingsRequest {
  member: IRetirementPerson
  memberInputs: IRetirementSavingsPersonInputs
  spouse: IRetirementPerson
  spouseInputs: IRetirementSavingsPersonInputs
  ratesOfReturn: IRatesOfReturn
}

const differ = JSON.stringify
const analyzeSavings = _.memoize(savingsGrinder, differ)

class PersonSavingsAnalysis {
  balances: number[] // ???
}

function savingsGrinder(req: ISavingsWorkbookRunCalculationsRequest): Promise<any> {
  return runCalculator(req)
    .then((extractors) => {
      const member = req.member
      const spouse = req.spouse

      function analyze(person: IRetirementPerson, inputs: IRetirementSavingsPersonInputs, section: SavingsDAOPersonExtractors) {
        const balances = section.getAllBalances()
        const levelWithdrawalAmount = section.levelWithdrawalAmount
        const finalBalance = section.finalBalance
        const getBalances = (x) => section.getBalances(x)

        const startContributions = DateCalcs.toCalcDate(utils.todaysDate())
        const annualContributions: number = inputs.annualContributions && inputs.annualContributions.amount ? inputs.annualContributions.amount : 0
        let stopContributions: moment.Moment
        if (isValidPerson(person) && inputs.annualContributions && inputs.annualContributions.stopWhen) {
          stopContributions = DateCalcs.toCalcDate(dateAtAge(person, inputs.annualContributions.stopWhen))
        } else {
          stopContributions = utils.todaysDate().subtract(1, 'day')
        }

        const savingsDuration = moment.duration(stopContributions.diff(startContributions))

        const totalFixedBalances = _(inputs.fixedBalances).valuesIn().sum()

        const savings = {
          startContributions, stopContributions, savingsDuration,
          annualContributions,
          totalFixedBalances, totalNewSavings: 0, totalSavings: 0
        }

        savings.totalNewSavings = newSavingsAsOfDuration(annualContributions, savingsDuration)
        savings.totalSavings = totalFixedBalances + savings.totalNewSavings

        let monthlyWithdrawalAmount: number = 0

        let firstBalanceIndex: number
        let firstBalanceInfo: ISavingsBalances

        let lastBalanceIndex: number
        let lastBalanceInfo: ISavingsBalances

        let startWithdrawals: moment.Moment = utils.todaysDate()
        let stopWithdrawals: moment.Moment
        let withdrawalsDuration: moment.Duration
        let firstWithdrawalIndex: number
        let firstWithdrawalInfo: ISavingsBalances
        let lastWithdrawalIndex: number
        let lastWithdrawalInfo: ISavingsBalances
        let savingsAndWithdrawalsSlice: ISavingsBalances[] = []
        let withdrawalSlice: ISavingsBalances[] = []

        do {
          if (!inputs.withdrawals.startWhen) { break }

          // should this be taken from the output of the sheet? (should be the same)
          monthlyWithdrawalAmount = inputs.withdrawals.monthlyAmount || 0

          firstBalanceIndex = _.findIndex(balances, nonZeroBalance)
          if (firstBalanceIndex < 0) { break }
          firstBalanceInfo = balances[firstBalanceIndex]

          lastBalanceIndex = _.findLastIndex(balances, nonZeroBalance)
          if (lastBalanceIndex < 0) { break }
          lastBalanceInfo = balances[lastBalanceIndex]
          savingsAndWithdrawalsSlice = _.slice(balances, firstBalanceIndex, lastBalanceIndex + 1)

          firstWithdrawalIndex = _.findIndex(balances, nonZeroAllowedWithdrawal)
          if (firstWithdrawalIndex < 0) {
            // if we didn't find a withdrawal, then use the one at first withdrawal date
            startWithdrawals = dateAtAge(person, inputs.withdrawals.startWhen)
            firstWithdrawalIndex = section.findRowIndex(startWithdrawals)
          }
          firstWithdrawalInfo = balances[firstWithdrawalIndex]
          startWithdrawals = firstWithdrawalInfo.when

          debug('start withdrawals for ' + person.firstName + ': ' + firstWithdrawalInfo.when.toISOString())

          lastWithdrawalIndex = _.findLastIndex(balances, nonZeroAllowedWithdrawal)
          // if we can't find any withdrawals, find the last time a balance amount was determined
          if (lastWithdrawalIndex < 0) {
            lastWithdrawalIndex = _.findLastIndex(balances, nonZeroAmount)
            if (lastWithdrawalIndex < 1) { break }
          }
          lastWithdrawalInfo = balances[lastWithdrawalIndex] //_.findLast(balances, nonZeroAllowedWithdrawal)
          withdrawalSlice = _.slice(balances, firstWithdrawalIndex, lastWithdrawalIndex + 1)
          savingsAndWithdrawalsSlice = _.slice(balances, firstBalanceIndex, lastWithdrawalIndex + 1)
          stopWithdrawals = lastWithdrawalInfo.when

          withdrawalsDuration = moment.duration(lastWithdrawalInfo.when.diff(firstWithdrawalInfo.when))

          // tslint:disable-next-line:no-console
          //debug('stop withdrawals for ' + req.person.firstName + ': ' + lastWithdrawalInfo.when.toISOString())

        } while (false);

        const withdrawals = {
          monthlyWithdrawalAmount,
          startWithdrawals,
          stopWithdrawals,
          withdrawalsDuration
        }

        function balanceAsOf(when: moment.Moment): number {
          const b = section.getBalances(when)
          return b ? b.amount : 0
        }

        return {
          firstName: person.firstName,
          balances,
          savings,
          withdrawals,
          firstBalanceInfo,
          firstBalanceIndex,
          lastBalanceInfo,
          lastBalanceIndex,
          //firstWithdrawalIndex,
          firstWithdrawalInfo,
          lastWithdrawalInfo,
          //lastWithdrawalIndex,
          savingsAndWithdrawalsSlice,
          withdrawalSlice,
          balanceAsOf,
          levelWithdrawalAmount,
          finalBalance,
          getBalances
        }
      }

      return {
        member: analyze(member, req.memberInputs, extractors.member),
        spouse: analyze(spouse, req.spouseInputs, extractors.spouse)
      }
    })
}

export {
  IAnalyzeSavingsRequest,
  analyzeSavings,
  getChartData,
  nonZeroBalance,
  nonZeroAllowedWithdrawal,
  whenSameDate
}



// WEBPACK FOOTER //
// ./src/app/calcs/RetirementSavingsAnalyzer/index.ts